<script setup lang="ts">
import {ref, watch, inject, computed} from 'vue';
import {useStore} from 'vuex';
import {tracker} from '@teemill/common/plugins';
import {ProductProvider} from '@teemill/modules/product';
import {Product, LiteProduct} from '@teemill/common/models';
import type {Theme} from '@teemill/modules/theme-builder';

import {captureException} from '@sentry/vue';

import ProductGrid from './ProductGrid.vue';

const props = withDefaults(
  defineProps<{
    product?: Product;
    divisionId: number;
    titleText?: string;
    titleAlignment?: 'left' | 'center';
    algorithm?: string;
  }>(),
  {
    titleText: 'Other products you might like',
    titleAlignment: 'center',
    algorithm: 'fast',
  }
);

const store = useStore();
const theme = inject<() => Theme | undefined>('theme', () => undefined);

const collectionFilter = computed(() => {
  const scope = theme()?.get('productPage.recommendations.scope');

  return scope === 'collection'
    ? // @ts-expect-error - collection exists on the product object, but not in the type
      props.product?.collection?.urlName
    : undefined;
});

const products = ref<LiteProduct[]>([]);

watch(
  () => props.product,
  () => {
    if (!props.product) {
      return;
    }

    let version = 'keyword';

    if (store.state.subdomain.productEmbeddingVersion) {
      version = 'vector';
    }

    ProductProvider.list(
      props.divisionId,
      8,
      props.product ? null : 'bestsellerOrder',
      props.algorithm,
      props.product ? props.product.urlName : null,
      version,
      collectionFilter.value
    )
      .then(data => {
        products.value = data;
      })
      .catch(error => {
        captureException(error);
      });
  },
  {immediate: true}
);
</script>

<template>
  <product-grid
    :products="products ?? []"
    :title-text="titleText"
    :title-alignment="titleAlignment"
    @click="tracker.event('click-related-product')"
  />
</template>
